/**
 * @file: This file holds the theme of the manager.
 *
 * Bootstrap reads data-bs-theme on the html element, and client/style/tokens.less
 * gives every colour a second value under the same attribute. One attribute
 * changes the whole page. The operator picks the theme in the menu and the
 * choice has to survive a reload, so the choice is in the storage of the
 * browser.
 *
 * The functions take the root element, the storage and the answer of the media
 * query as parameters. Thus a spec needs no browser, and the manager shell is
 * the one place that reads the real window.
 */

/** The key of the choice in the storage of the browser. */
const STORAGE_KEY = 'jumbotron-theme'

/**
 * The three choices of the menu. 'system' follows the setting of the browser,
 * so the app changes with the machine at sunset.
 */
const THEMES = Object.freeze(['system', 'light', 'dark'])

/** The choice for an operator who has picked nothing, and for a bad value. */
const DEFAULT_THEME = 'system'

/** The choice, or the default for a storage that holds a name nobody knows. */
function readTheme (storage) {
  const stored = storage ? storage.getItem(STORAGE_KEY) : null

  return THEMES.includes(stored) ? stored : DEFAULT_THEME
}

/**
 * The theme to write on the page. 'system' is not a value of the attribute, so
 * the answer of the media query decides it here.
 */
function resolveTheme (theme, prefersDark) {
  const chosen = THEMES.includes(theme) ? theme : DEFAULT_THEME

  if (chosen === 'system') {
    return prefersDark ? 'dark' : 'light'
  }

  return chosen
}

/**
 * Write the theme on the page and keep the choice. The page reads the resolved
 * theme, and the storage keeps the choice of the operator, because 'system'
 * has to stay 'system' after a reload.
 */
function applyTheme (theme, { root, storage, prefersDark }) {
  const chosen = THEMES.includes(theme) ? theme : DEFAULT_THEME

  if (root) {
    root.dataset.bsTheme = resolveTheme(chosen, prefersDark)
  }

  if (storage) {
    storage.setItem(STORAGE_KEY, chosen)
  }
}

/**
 * Remove the theme from the page. The player pages do not read the tokens, so
 * the shell takes the attribute with it when it goes away.
 */
function clearTheme ({ root }) {
  if (root) {
    delete root.dataset.bsTheme
  }
}

export { THEMES, STORAGE_KEY, readTheme, resolveTheme, applyTheme, clearTheme }
